import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import React, {useMemo, useRef} from 'react';
import {twMerge} from 'tailwind-merge';

import {usePanelSearchDebouncedContext} from '../../state/panelbank/PanelSearchContextProvider';
import {useViewAction} from '../../state/views/hooks';
import {pinSection} from '../../state/views/panelBankConfig/actions';
import {useWorkspaceRefsContext} from '../Workspace/WorkspaceRefsContext';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {usePanelBankSectionIsOpen} from './hooks/panelBankSectionIsOpenHooks';
import {PanelBankSectionHeader} from './PanelBankSectionHeader2';
import {SectionWithRef} from './types';
import {getIsHiddenPanelsSection} from './useIsSectionVisible';
import {useSectionNameMatch} from './useSectionNameMatch';
import {useSectionPanels} from './useSectionPanels';
import {getDndSortableSectionId} from './utils';
import {WorkspaceFlowLayout} from './WorkspaceFlowLayout';
import {WorkspaceSectionLayoutConfigs} from './WorkspaceSectionLayoutConfigs';

/**
 * This component assumes that a section should be rendered in the workspace.
 */
export const SortablePanelBankSection = ({
  idx,
  isProjectReadOnly,
  numSections,
  sectionWithRef,
}: {
  idx: number;
  isProjectReadOnly: boolean;
  numSections: number;
  sectionWithRef: SectionWithRef;
}) => {
  const sectionComponentRef = useRef<HTMLDivElement>(null);

  const sectionRef = sectionWithRef.ref;
  const {panelBankConfigRef} = useWorkspaceRefsContext();
  const pinPanelBankSection = useViewAction(panelBankConfigRef, pinSection);

  const {debouncedSearchRegex: searchRegex} = usePanelSearchDebouncedContext();
  const isSectionNameSearchMatch = useSectionNameMatch(
    sectionWithRef.name,
    searchRegex
  );

  const {activePanels, filteredPanels} = useSectionPanels(
    sectionWithRef,
    searchRegex,
    isSectionNameSearchMatch
  );

  const isHiddenPanelsSection = useMemo(() => {
    return getIsHiddenPanelsSection({
      idx,
      numSections,
      sectionName: sectionWithRef.name,
    });
  }, [idx, numSections, sectionWithRef.name]);

  const {sectionIsOpen: sectionIsExpanded, shouldRenderSection} =
    usePanelBankSectionIsOpen({
      panelBankSectionConfigRef: sectionRef,
    });

  const {setNodeRef, transform, transition, isOver} = useSortable({
    id: getDndSortableSectionId(sectionWithRef.ref),
    data: {
      // This helps indicate if we're dragging a section or panel
      type: 'section',
      sectionRef: sectionWithRef.ref,
      filteredPanelsCount: filteredPanels.length,
    },
  });

  return (
    <SectionSettingsContextProvider sectionRef={sectionRef}>
      <div
        ref={setNodeRef}
        className={twMerge(
          'border-b-[1px] border-moon-300',
          sectionIsExpanded ? 'pb-16' : 'pb-0',
          // If a panel is being dragged over an empty section, highlight the section
          isOver && filteredPanels.length === 0 && 'bg-teal-350/[0.24]'
        )}
        style={{
          transform: CSS.Transform.toString(transform),
          transition,
        }}>
        <PanelBankSectionHeader
          activePanelCount={activePanels.length}
          isHiddenPanelsSection={isHiddenPanelsSection}
          isPinned={sectionWithRef.pinned}
          isProjectReadOnly={isProjectReadOnly}
          isSectionNameSearchMatch={isSectionNameSearchMatch}
          panelBankSectionConfigRef={sectionRef}
          panelSearchMatchCount={filteredPanels.length}
          pinPanelBankSection={pinPanelBankSection}
          sectionComponentRef={sectionComponentRef}
          sectionLayoutType={sectionWithRef.type}
          sectionName={sectionWithRef.name}
        />
        {/* 
            Two reasons for using display: none here:
            1. we don't want to render section if it's never opened
            2. we don't want to re-mount an open section if we close and re-open it
            Doing this allows us avoid triggering panel loaders which gives us a 
            smoother and more performant experience for users.
          */}
        <div className={twMerge(sectionIsExpanded ? 'contents' : 'hidden')}>
          {/*
              We don't want to render the section if it's never opened, but we also don't want
              to re-mount an open section if you collapse and re-open it.
              Take a look at what `shouldRenderSection` does inside of `usePanelBankSectionIsOpen`
           */}
          {shouldRenderSection && (
            <WorkspaceSectionLayoutConfigs
              numTotalPanelsInSection={filteredPanels.length}
              sectionRef={sectionRef}>
              <WorkspaceFlowLayout
                filteredPanels={filteredPanels}
                isProjectReadOnly={isProjectReadOnly}
                panelBankSectionConfigRef={sectionRef}
              />
            </WorkspaceSectionLayoutConfigs>
          )}
        </div>
      </div>
    </SectionSettingsContextProvider>
  );
};
